import { baseApi } from './baseApi';
import type { MenuIngest, MenuDraftItem, MenuIngestConfirmResult } from '../types/menuIngest';

/**
 * Ingesta del menú de papel con IA.
 *
 * El flujo es: abrir una carga → subir las páginas UNA POR UNA → revisar y
 * corregir el borrador → confirmar. Las páginas van de a una porque cada una es
 * una llamada al modelo: así el usuario ve progreso real ("página 2 de 4") en
 * vez de esperar dos minutos frente a una pantalla quieta.
 *
 * Ningún endpoint de aquí crea productos salvo `confirm`.
 */
export const menuIngestApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /** Las cargas sin confirmar, para retomar una a medias. */
    getPendingMenuIngests: builder.query<MenuIngest[], void>({
      query: () => '/menu-ingest',
      providesTags: [{ type: 'MenuIngest' as const, id: 'LIST' }],
    }),

    getMenuIngest: builder.query<MenuIngest, string>({
      query: (id) => `/menu-ingest/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'MenuIngest' as const, id }],
    }),

    startMenuIngest: builder.mutation<MenuIngest, void>({
      query: () => ({ url: '/menu-ingest', method: 'POST' }),
      invalidatesTags: [{ type: 'MenuIngest' as const, id: 'LIST' }],
    }),

    /**
     * Sube y lee UNA página.
     *
     * `analysisStatus` de la página puede venir `unreadable` o `unavailable` y
     * **eso no es un error**: son 200, la imagen quedó guardada y el resto del
     * borrador sigue en pie. Sólo un 400 significa que el archivo no sirve.
     */
    addMenuIngestPage: builder.mutation<
      MenuIngest,
      { ingestionId: string; file: Blob; fileName: string; totalPages: number }
    >({
      query: ({ ingestionId, file, fileName, totalPages }) => {
        const formData = new FormData();
        formData.append('file', file, fileName);
        formData.append('totalPages', String(totalPages));
        return {
          url: `/menu-ingest/${ingestionId}/pages`,
          method: 'POST',
          body: formData,
          // Sin Content-Type a mano: el browser tiene que poner el boundary del
          // multipart. fetchBaseQuery detecta FormData y no lo pisa.
        };
      },
      invalidatesTags: (_r, _e, { ingestionId }) => [
        { type: 'MenuIngest' as const, id: ingestionId },
      ],
    }),

    /** Guarda las correcciones. Reemplaza el borrador completo y lo revalida. */
    saveMenuIngestDraft: builder.mutation<
      MenuIngest,
      { ingestionId: string; items: MenuDraftItem[] }
    >({
      query: ({ ingestionId, items }) => ({
        url: `/menu-ingest/${ingestionId}/draft`,
        method: 'PUT',
        body: { items },
      }),
      invalidatesTags: (_r, _e, { ingestionId }) => [
        { type: 'MenuIngest' as const, id: ingestionId },
      ],
    }),

    confirmMenuIngest: builder.mutation<MenuIngestConfirmResult, string>({
      query: (ingestionId) => ({
        url: `/menu-ingest/${ingestionId}/confirm`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, ingestionId) => [
        { type: 'MenuIngest' as const, id: ingestionId },
        { type: 'MenuIngest' as const, id: 'LIST' },
        // La carta acaba de cambiar por completo. La MISMA lista que invalida el import por
        // CSV, que es literalmente el mismo camino de creación: si divergen, una de las dos
        // pantallas se queda mirando datos viejos y nadie sabe por qué. MenuEditor incluida —
        // el lienzo del menú digital se arma de estos productos.
        'Product',
        'Category',
        'MenuEditor',
      ],
    }),

    discardMenuIngest: builder.mutation<void, string>({
      query: (ingestionId) => ({ url: `/menu-ingest/${ingestionId}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'MenuIngest' as const, id: 'LIST' }],
    }),
  }),
});

/**
 * URL de la imagen de una página. No pasa por RTK Query porque es un binario que
 * se consume en un `<img>`; el endpoint exige token, así que se sirve vía fetch
 * en el componente que la muestra.
 */
export const menuIngestPageImagePath = (ingestionId: string, pageId: string) =>
  `/menu-ingest/${ingestionId}/pages/${pageId}/image`;

export const {
  useGetPendingMenuIngestsQuery,
  useGetMenuIngestQuery,
  useStartMenuIngestMutation,
  useAddMenuIngestPageMutation,
  useSaveMenuIngestDraftMutation,
  useConfirmMenuIngestMutation,
  useDiscardMenuIngestMutation,
} = menuIngestApi;
